import React, { useEffect, useState } from 'react';
import { Button, Modal, Picker, TextField, Typography } from '@riac/design-system';
import { Item } from '@react-stately/collections';
import { PhoneVerify } from 'src/components/Phones/PhoneVerify/PhoneVerify';
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';

type CachedEditPhoneData = [] | [number, string, number];

export const showFormStatuses = ['create', 'edit'];

export const VerifyModal = ({ isOpen, closeModal, phone, updatePhoneList }) => {
  return (
    <Modal
      isOpen={isOpen}
      isDismissable
      showCloseButton
      title='Phone Verification'
      onClose={closeModal}
    >
      <PhoneVerify
        updatePhoneList={updatePhoneList}
        phone={{
          Phone: phone.phoneNum,
          PhoneID: phone.phoneID,
          VerifiedID: phone.verifiedID,
        }}
      />
    </Modal>
  );
};

export const PhoneForm = ({
  phones,
  phoneAction,
  setPhoneAction,
  selectedPhone,
  setSelectedPhone,
  getPhones,
  verifyOpen,
  setVerifyOpen,
}) => {
  // 2. Add state for country code and the list of countries
  const [countryCode, setCountryCode] = useState<string>('US'); // Default to US
  const [countries, setCountries] = useState([]);
  
  const [phoneNum, setPhoneNum] = useState<string>('');
  const [phoneExt, setPhoneExt] = useState<string>('');
  const [phoneTypes, setPhoneTypes] = useState([]);
  const [reverifyConfirm, setReverifyConfirm] = useState(false);
  const [editPhoneData, setEditPhoneData] = useState<CachedEditPhoneData>([]);
  const [errors, setErrors] = useState({ phoneNum: '', phoneExt: '' });

  // 3. Populate the country list on component mount
  useEffect(() => {
    const countryNames = getCountries(); // e.g., ['US', 'GB', 'DE']
    const countryData = countryNames
      .map((code) => ({
        code,
        name: new Intl.DisplayNames(['en'], { type: 'region' }).of(code),
        dialCode: `+${getCountryCallingCode(code)}`,
      }))
      .sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically
    setCountries(countryData);
  }, []);

  async function getPhoneTypes() {
    const typeReq = await fetch('/api/account/phone/getTypes', {
      credentials: 'include',
    });
    const typeList = await typeReq.json();
    setPhoneTypes(typeList);
  }

  async function addPhone(type, number) {
    await fetch('/api/account/phone/newPhone', {
      credentials: 'include',
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        phoneTypeID: type,
        phoneNumber: number,
      }),
    });
    setPhoneAction(undefined);
    getPhones();
  }

  async function updatePhone(type, number, id) {
    await fetch('/api/account/phone/updatePhone', {
      credentials: 'include',
      method: 'PUT',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        phoneTypeID: type,
        phoneNumber: number,
        phoneID: id,
      }),
    });
    setPhoneAction(undefined);
    getPhones();
  }
  
  const validateForm = () => {
    const newErrors = { phoneNum: '', phoneExt: '' };
    
    if (!phoneNum) {
      newErrors.phoneNum = 'Phone number is required.';
    } else {
      // Use the library to parse and validate the number for the selected country
      const phoneNumber = parsePhoneNumberFromString(phoneNum, countryCode as any);
      if (!phoneNumber || !phoneNumber.isValid()) {
        newErrors.phoneNum = 'Please enter a valid phone number for the selected country.';
      }
    }

    // Extension validation remains the same
    const cleanedExt = phoneExt.replace(/\D/g, '');
    if (phoneExt && cleanedExt !== phoneExt) {
        newErrors.phoneExt = 'Extension must only contain numbers.';
    } else if (cleanedExt.length > 6) {
        newErrors.phoneExt = 'Extension cannot be longer than 6 digits.';
    }
    
    setErrors(newErrors);
    return !newErrors.phoneNum && !newErrors.phoneExt;
  };

  function handleSubmit() {
    if (!validateForm()) {
        return;
    }

    const phoneNumber = parsePhoneNumberFromString(phoneNum, countryCode as any);
    let finalPhoneNumber = phoneNumber.format('E.164'); // e.g., +13125550123
    
    if (phoneExt) {
      finalPhoneNumber += ` Ext:${phoneExt.replace(/\D/g, '')}`;
    }

    const typeID = parseInt(currentPhoneType.PhoneTypeID + '');

    if (phoneAction === 'create') {
      addPhone(typeID, finalPhoneNumber);
    }
    if (phoneAction === 'edit') {
      const matchPhone = phones.find((phone) => phone.PhoneID == selectedPhone.phoneID);
      if (matchPhone && matchPhone.VerifiedID == 1) {
        setEditPhoneData([typeID, finalPhoneNumber, selectedPhone.phoneID]);
        setReverifyConfirm(true);
      } else {
        updatePhone(typeID, finalPhoneNumber, selectedPhone.phoneID);
      }
    }
  }

  useEffect(() => {
    getPhoneTypes();
  }, []);

  useEffect(() => {
    setErrors({ phoneNum: '', phoneExt: '' });
    if (phoneAction === 'edit' && selectedPhone.phoneNum) {
        const fullNumber = selectedPhone.phoneNum;
        // Attempt to parse the full number string (which might include an extension)
        const phoneNumber = parsePhoneNumberFromString(fullNumber);

        if (phoneNumber) {
            setCountryCode(phoneNumber.country);
            setPhoneNum(phoneNumber.nationalNumber);
            // The library doesn't parse extensions, so we extract it manually
            const extMatch = fullNumber.match(/(?:Ext|ext|x|X)[:.\s]?\s*(\d+)/);
            setPhoneExt(extMatch ? extMatch[1] : (selectedPhone.ext || ''));
        } else {
            // Fallback for numbers the library can't parse (e.g., old malformed data)
            setPhoneNum(fullNumber);
            setPhoneExt(selectedPhone.ext || '');
        }
    } else {
      setCountryCode('US');
      setPhoneNum('');
      setPhoneExt('');
    }
  }, [phoneAction, selectedPhone]);

  const currentPhoneType = phoneTypes.find((x) => x.PhoneTypeID == selectedPhone.PhoneTypeID);

  const handlePhoneInputChange = (val: string) => {
      setPhoneNum(val);
      if (errors.phoneNum) {
          setErrors(prev => ({...prev, phoneNum: ''}));
      }
  };

  const handleExtInputChange = (val: string) => {
      setPhoneExt(val);
      if (errors.phoneExt) {
          setErrors(prev => ({...prev, phoneExt: ''}));
      }
  };
  
  const handleCancel = () => {
    setPhoneAction(undefined);
    setSelectedPhone({ PhoneTypeID: 1, phoneID: 0, phoneNum: '', verifiedID: 0, ext: '' });
    setErrors({ phoneNum: '', phoneExt: '' });
  };

  return (
    <>
      {(phoneAction === 'create' || phoneAction === 'edit') ? (
        <div className='m-auto max-w-[590px] px-5 py-8 flex-col flex gap-3 border-1 border-surface-darker'>
          <Typography heading size='sm' weight='bold'>{phoneAction === 'create' ? 'Add' : 'Edit'} phone number</Typography>
          <div className='flex flex-col gap-5'>
            <Picker
              label='Type'
              selectedKey={currentPhoneType?.PhoneTypeID.toString()}
              onSelectionChange={(value) => setSelectedPhone((prev) => Object.assign(
                {},
                prev,
                {
                  PhoneTypeID: value,
                },
              ))}
              isRequired
            >
              {phoneTypes.filter((type) => !(phoneExt != '' && type.PhoneTypeID == 2)).map((type) => (
                <Item key={type.PhoneTypeID}>{type.PhoneType}</Item>
              ))}
            </Picker>
            <div className="flex gap-3 items-start flex-wrap">
              <Picker
                label="Country"
                items={countries}
                selectedKey={countryCode}
                onSelectionChange={(key) => setCountryCode(key as string)}
                className="min-w-[200px] w-full flex-1" // Adjust width as needed
              >
                {(item) => <Item key={item.code}>{`${item.name} (${item.dialCode})`}</Item>}
              </Picker>
              <TextField
                isRequired
                type='tel'
                autoComplete='tel'
                label='Phone Number'
                value={phoneNum}
                onChange={handlePhoneInputChange}
                errorMessage={errors.phoneNum}
                isInvalid={!!errors.phoneNum}
                className='min-w-[200px] flex-[55]'
              />
            </div>

            {currentPhoneType?.PhoneTypeID != 2 && (
              <TextField
                label='Extension'
                value={phoneExt}
                onChange={handleExtInputChange}
                errorMessage={errors.phoneExt}
                isInvalid={!!errors.phoneExt}
              />
            )}
          </div>
          <div className='pt-3 gap-5 flex flex-col'>
            <Button onPress={handleSubmit}>
              Save Phone
            </Button>
            <Button
              type='button'
              variant='secondary'
              onPress={handleCancel}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : ''}
      {verifyOpen
        && <VerifyModal
          updatePhoneList={getPhones}
          isOpen={verifyOpen}
          closeModal={() => {
            setVerifyOpen(false);
            getPhones();
          }}
          phone={selectedPhone}
        />
      }
      <Modal
        isOpen={reverifyConfirm}
        isDismissable
        showCloseButton
        title='Re-verification Required'
        footerButtons={<>
          <Button size='lg' onPress={() => {
            if(editPhoneData.length > 0) { // Check length to be safe
              updatePhone(...editPhoneData);
            }
            setEditPhoneData([]);
            setReverifyConfirm(false);
          }}>
            Continue
          </Button>
          <Button size='lg' variant='secondary' onPress={() => setReverifyConfirm(false)}>
            Cancel
          </Button>
        </>}
      >
        Editing this number will require it to be re-verified. Do you want to continue?
      </Modal>
    </>);
};